import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Typography,
  CircularProgress,
  Alert,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useDispatch } from "react-redux";
import useAuth from "../../hooks/useAuth";
import { setDialog } from "../../features/dialog/dialogSlice";
import {
  useGetEventRegistrationsQuery,
  useLazyGetRegistrationHealthDataQuery,
  useUpdateRegistrationStatusMutation,
  useUpdatePaymentStatusMutation,
  useDeleteRegistrationMutation,
  useBulkUpdatePaymentStatusMutation,
} from "../../features/eventRegistrations/eventRegistrationsApiSlice";
import { httpCommonAuth } from "../../app/api/axios/http.service";
import EventRegistrationsSepaDialog from "./EventRegistrationsSepaDialog";
import EditRegistrationPricingDialog from "./EditRegistrationPricingDialog";

const STATUS_CONFIG = {
  submitted: { label: "Eingereicht", color: "warning" },
  confirmed: { label: "Bestätigt", color: "success" },
  waitlist: { label: "Warteliste", color: "default" },
  cancelled: { label: "Storniert", color: "error" },
  draft: { label: "Entwurf", color: "default" },
};

const PAYMENT_CONFIG = {
  unpaid: { label: "Offen", color: "error" },
  invoice_sent: { label: "Rechnung versendet", color: "warning" },
  sepa_exported: { label: "SEPA exportiert", color: "info" },
  paid: { label: "Bezahlt", color: "success" },
  refunded: { label: "Erstattet", color: "default" },
  not_applicable: { label: "Nicht fällig", color: "default" },
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    Number(value) || 0
  );

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const toAmount = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return number;
};

const getRegistrationPricingBreakdown = (registration = {}) => {
  const snapshot = registration?.pricingSnapshot || {};

  const grossAmount = toAmount(snapshot.grossAmount);
  const siblingDiscountAmount = toAmount(snapshot.siblingDiscountApplied);
  const earlyBirdDiscountAmount = toAmount(snapshot.earlyBirdDiscountApplied);
  const adminDiscountAmount = toAmount(snapshot.adminDiscountApplied);
  const totalAmount = toAmount(
    registration?.totalAmount ?? snapshot.totalAmount ?? 0
  );
  const memberDiscountAmount = Math.max(
    0,
    grossAmount -
      siblingDiscountAmount -
      earlyBirdDiscountAmount -
      adminDiscountAmount -
      totalAmount
  );

  return {
    grossAmount,
    memberDiscountAmount,
    siblingDiscountAmount,
    earlyBirdDiscountAmount,
    adminDiscountAmount,
    totalAmount,
    hasBreakdown:
      grossAmount > 0 ||
      memberDiscountAmount > 0 ||
      siblingDiscountAmount > 0 ||
      earlyBirdDiscountAmount > 0 ||
      adminDiscountAmount > 0,
  };
};

export default function EventRegistrationsTab({ event }) {
  const dispatch = useDispatch();
  const { isAdmin, isSuperAdmin } = useAuth();
  const eventId = event?._id;
  const canViewSensitiveData = isAdmin || isSuperAdmin;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState([]);
  const [sepaDialogOpen, setSepaDialogOpen] = useState(false);
  const [bulkPaidDialogOpen, setBulkPaidDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [registrationToDelete, setRegistrationToDelete] = useState(null);
  const [healthDialogOpen, setHealthDialogOpen] = useState(false);
  const [healthRegistration, setHealthRegistration] = useState(null);
  const [pricingEditReg, setPricingEditReg] = useState(null);

  const queryParams = {
    eventId,
    page: page + 1,
    limit: rowsPerPage,
    ...(statusFilter && { status: statusFilter }),
    ...(paymentFilter && { paymentStatus: paymentFilter }),
    ...(search && { search }),
  };

  const { data, isFetching, error } = useGetEventRegistrationsQuery(queryParams, {
    skip: !eventId,
  });

  const [updateStatus, { isLoading: isUpdatingStatus }] = useUpdateRegistrationStatusMutation();
  const [updatePayment, { isLoading: isUpdatingPayment }] = useUpdatePaymentStatusMutation();
  const [deleteRegistration, { isLoading: isDeletingRegistration }] = useDeleteRegistrationMutation();
  const [bulkUpdatePaymentStatus, { isLoading: isBulkUpdatingPayment }] =
    useBulkUpdatePaymentStatusMutation();
  const [loadHealthData, { data: healthDataResponse, isFetching: isFetchingHealthData }] =
    useLazyGetRegistrationHealthDataQuery();

  const registrations = data?.registrations || [];
  const total = data?.total || 0;
  const summary = data?.summary;
  const selectedRowsOnCurrentPage = registrations.filter((registration) =>
    selected.includes(registration._id)
  );
  const selectedSepaExportedCount = selectedRowsOnCurrentPage.filter(
    (registration) => registration?.paymentStatus === "sepa_exported"
  ).length;

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(0);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(registrations.map((r) => r._id));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleStatusChange = async (regId, newStatus) => {
    try {
      await updateStatus({ eventId, regId, registrationStatus: newStatus }).unwrap();
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Status konnte nicht geändert werden." },
        })
      );
    }
  };

  const handlePaymentChange = async (regId, newPaymentStatus) => {
    try {
      await updatePayment({ eventId, regId, paymentStatus: newPaymentStatus }).unwrap();
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: { errorText: e?.data?.message || "Zahlungsstatus konnte nicht geändert werden." },
        })
      );
    }
  };

  const handleBulkMarkSepaAsPaid = async () => {
    if (!eventId) return;
    const hasSelection = selected.length > 0;
    try {
      const result = await bulkUpdatePaymentStatus({
        eventId,
        registrationIds: hasSelection ? selected : [],
        fromPaymentStatus: "sepa_exported",
        paymentStatus: "paid",
        note: "Bulk-Aktualisierung nach erfolgreicher Lastschrift",
      }).unwrap();

      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Bulk-Update erfolgreich",
          data: {
            successText: `${result?.updatedCount || 0} Buchung(en) wurden auf „Bezahlt“ gesetzt.`,
          },
        })
      );
      setBulkPaidDialogOpen(false);
      setSelected([]);
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Bulk-Update fehlgeschlagen",
          data: {
            errorText:
              error?.data?.message ||
              "Die ausgewählten Buchungen konnten nicht aktualisiert werden.",
          },
        })
      );
    }
  };

  const openDeleteDialog = (registration) => {
    setRegistrationToDelete(registration);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRegistrationToDelete(null);
  };

  const closeHealthDialog = () => {
    setHealthDialogOpen(false);
    setHealthRegistration(null);
  };

  const handleDeleteRegistration = async () => {
    if (!registrationToDelete?._id) return;
    try {
      await deleteRegistration({
        eventId,
        regId: registrationToDelete._id,
      }).unwrap();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Anmeldung gelöscht",
          data: { successText: "Die Anmeldung wurde dauerhaft entfernt." },
        })
      );
      closeDeleteDialog();
      setSelected((prev) => prev.filter((id) => id !== registrationToDelete._id));
    } catch (e) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Löschen fehlgeschlagen",
          data: { errorText: e?.data?.message || "Anmeldung konnte nicht gelöscht werden." },
        })
      );
    }
  };

  const handleOpenHealthDialog = async (registration) => {
    if (!registration?._id || !canViewSensitiveData) return;
    setHealthRegistration(registration);
    setHealthDialogOpen(true);
    try {
      await loadHealthData({
        eventId,
        regId: registration._id,
      }).unwrap();
    } catch (e) {
      closeHealthDialog();
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Zugriff fehlgeschlagen",
          data: {
            errorText:
              e?.data?.message ||
              "Sensible Daten konnten nicht geladen werden.",
          },
        })
      );
    }
  };

  const handleExport = (format = "csv") => {
    const params = new URLSearchParams({
      ...(statusFilter && { status: statusFilter }),
      ...(paymentFilter && { paymentStatus: paymentFilter }),
      ...(search && { search }),
      format,
    });
    const url = `/events/${eventId}/registrations/export?${params.toString()}`;
    const isExcel = format !== "csv";

    httpCommonAuth
      .get(url, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: isExcel ? "application/vnd.ms-excel;charset=utf-8;" : "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `anmeldungen-${eventId}-${new Date().toISOString().slice(0, 10)}.${isExcel ? "xls" : "csv"}`;
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => {
        dispatch(
          setDialog({
            type: "ERROR",
            title: "Fehler",
            data: {
              errorText: `${isExcel ? "Excel" : "CSV"}-Export fehlgeschlagen.`,
            },
          })
        );
      });
  };

  return (
    <Box>
      {/* Toolbar */}
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} flex={1}>
            <form onSubmit={handleSearchSubmit} style={{ display: "flex", gap: 8, flex: 1 }}>
              <TextField
                size="small"
                placeholder="Suche nach Name, E-Mail, Nr."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                sx={{ flex: 1, maxWidth: 280 }}
              />
              <Button type="submit" variant="outlined" size="small">
                Suchen
              </Button>
            </form>

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">Alle</MenuItem>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Zahlung</InputLabel>
              <Select
                value={paymentFilter}
                label="Zahlung"
                onChange={(e) => { setPaymentFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">Alle</MenuItem>
                {Object.entries(PAYMENT_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Icon icon="mdi:file-delimited-outline" />}
              onClick={() => handleExport("csv")}
            >
              CSV
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Icon icon="mdi:file-excel-outline" />}
              onClick={() => handleExport("excel")}
            >
              Excel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<Icon icon="mdi:bank-transfer" />}
              onClick={() => setSepaDialogOpen(true)}
            >
              SEPA-Export
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<Icon icon="mdi:cash-check" />}
              onClick={() => setBulkPaidDialogOpen(true)}
              disabled={isBulkUpdatingPayment}
            >
              Bulk: SEPA → Bezahlt
            </Button>
          </Stack>
        </Stack>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            px: 2,
            py: 1.5,
            background:
              "linear-gradient(135deg, rgba(2,132,199,0.10) 0%, rgba(16,185,129,0.08) 100%)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ md: "center" }}
          >
            <Typography variant="body2" color="text.secondary">
              Workflow: SEPA-Export erzeugt die Lastschriftdatei und setzt den Status auf
              „SEPA exportiert“. Danach mit „Bulk: SEPA → Bezahlt“ gesammelt abschließen.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                color="info"
                label={`SEPA exportiert: ${summary?.sepaExportedCount || 0}`}
              />
              <Chip
                size="small"
                color="success"
                label={`Bezahlt: ${summary?.paidCount || 0}`}
              />
            </Stack>
          </Stack>
        </Box>

        {selected.length > 0 && (
          <Alert severity="info" icon={false}>
            {selected.length} Buchung(en) ausgewählt
            {selectedSepaExportedCount > 0
              ? ` • ${selectedSepaExportedCount} davon bereits „SEPA exportiert“`
              : ""}
          </Alert>
        )}
        {summary && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip size="small" label={`Gesamt: ${summary.totalRegistrations || 0}`} />
            <Chip size="small" color="success" label={`Bestätigt: ${summary.confirmedCount || 0}`} />
            <Chip size="small" label={`Warteliste: ${summary.waitlistCount || 0}`} />
            <Chip size="small" color="info" label={`SEPA exportiert: ${summary.sepaExportedCount || 0}`} />
            <Chip size="small" color="success" label={`Bezahlt: ${summary.paidCount || 0}`} />
            <Chip size="small" color="error" label={`Offen: ${summary.unpaidCount || 0}`} />
            <Chip size="small" label={`Umsatz: ${formatCurrency(summary.totalRevenue || 0)}`} />
          </Stack>
        )}
      </Stack>

      {/* Tabelle */}
      {error ? (
        <Alert severity="error">Anmeldungen konnten nicht geladen werden.</Alert>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selected.length > 0 && selected.length < registrations.length}
                      checked={registrations.length > 0 && selected.length === registrations.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Buchungsnr.</TableCell>
                  <TableCell>Name / E-Mail</TableCell>
                  <TableCell>Teilnehmer</TableCell>
                  <TableCell>Datum</TableCell>
                  <TableCell>Betrag</TableCell>
                  <TableCell>Preisdetails</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Zahlung</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : registrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Keine Anmeldungen gefunden</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  registrations.map((reg) => {
                    const pricingBreakdown = getRegistrationPricingBreakdown(reg);
                    return (
                      <TableRow
                        key={reg._id}
                        selected={selected.includes(reg._id)}
                        hover
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected.includes(reg._id)}
                            onChange={() => handleSelectOne(reg._id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {reg.registrationNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {reg.booker?.firstName} {reg.booker?.lastName}
                          </Typography>
                          {reg.booker?.email && (
                            <Typography variant="caption" color="text.secondary">
                              {reg.booker.email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{reg.participants?.length || 0}</TableCell>
                        <TableCell>{formatDate(reg.createdAt)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(reg.totalAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {!pricingBreakdown.hasBreakdown ? (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          ) : (
                            <Stack spacing={0.25}>
                              {pricingBreakdown.grossAmount > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  Brutto: {formatCurrency(pricingBreakdown.grossAmount)}
                                </Typography>
                              )}
                              {pricingBreakdown.memberDiscountAmount > 0 && (
                                <Typography variant="caption" color="success.main">
                                  Mitgliederrabatt: -{formatCurrency(pricingBreakdown.memberDiscountAmount)}
                                </Typography>
                              )}
                              {pricingBreakdown.siblingDiscountAmount > 0 && (
                                <Typography variant="caption" color="success.main">
                                  Geschwister: -{formatCurrency(pricingBreakdown.siblingDiscountAmount)}
                                </Typography>
                              )}
                              {pricingBreakdown.earlyBirdDiscountAmount > 0 && (
                                <Typography variant="caption" color="success.main">
                                  Frühbucher: -{formatCurrency(pricingBreakdown.earlyBirdDiscountAmount)}
                                </Typography>
                              )}
                              {pricingBreakdown.adminDiscountAmount > 0 && (
                                <Typography variant="caption" color="success.main">
                                  {reg?.adminDiscount?.label || "Admin"}: -
                                  {formatCurrency(pricingBreakdown.adminDiscountAmount)}
                                </Typography>
                              )}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" variant="standard">
                            <Select
                              value={reg.registrationStatus}
                              onChange={(e) => handleStatusChange(reg._id, e.target.value)}
                              disableUnderline
                              renderValue={(val) => (
                                <Chip
                                  label={STATUS_CONFIG[val]?.label || val}
                                  color={STATUS_CONFIG[val]?.color || "default"}
                                  size="small"
                                />
                              )}
                              disabled={isUpdatingStatus}
                            >
                              {["submitted", "confirmed", "waitlist", "cancelled"].map((s) => (
                                <MenuItem key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" variant="standard">
                            <Select
                              value={reg.paymentStatus}
                              onChange={(e) => handlePaymentChange(reg._id, e.target.value)}
                              disableUnderline
                              renderValue={(val) => (
                                <Chip
                                  label={PAYMENT_CONFIG[val]?.label || val}
                                  color={PAYMENT_CONFIG[val]?.color || "default"}
                                  size="small"
                                />
                              )}
                              disabled={isUpdatingPayment}
                            >
                              {Object.entries(PAYMENT_CONFIG).map(([k, v]) => (
                                <MenuItem key={k} value={k}>{v.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell align="right">
                          {event?.registrationType === "booking" &&
                            reg.selectedPricingRuleId && (
                              <Tooltip title="Preis bearbeiten">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => setPricingEditReg(reg)}
                                >
                                  <Icon icon="mdi:currency-eur" />
                                </IconButton>
                              </Tooltip>
                            )}
                          <Tooltip title="Anmeldung löschen">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDeleteDialog(reg)}
                            >
                              <Icon icon="mdi:trash-can-outline" />
                            </IconButton>
                          </Tooltip>
                          {canViewSensitiveData && (
                            <Tooltip title="Sensible Daten anzeigen">
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleOpenHealthDialog(reg)}
                                  disabled={
                                    isFetchingHealthData &&
                                    healthRegistration?._id === reg._id
                                  }
                                >
                                  <Icon icon="mdi:shield-account-outline" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Zeilen:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
          />
        </>
      )}

      {/* SEPA-Export-Dialog */}
      <EventRegistrationsSepaDialog
        open={sepaDialogOpen}
        onClose={() => setSepaDialogOpen(false)}
        event={event}
        selectedRegistrationIds={selected}
        onExported={() => setSelected([])}
      />

      {/* Bulk-Zahlungsstatus-Dialog */}
      <Dialog open={bulkPaidDialogOpen} onClose={() => setBulkPaidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>SEPA exportiert → Bezahlt</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              {selected.length > 0
                ? "Es werden nur ausgewählte Buchungen mit Status „SEPA exportiert“ auf „Bezahlt“ gesetzt."
                : "Es werden alle Buchungen dieser Veranstaltung mit Status „SEPA exportiert“ auf „Bezahlt“ gesetzt."}
            </Alert>
            {selected.length > 0 && selectedSepaExportedCount === 0 && (
              <Alert severity="warning">
                In der aktuellen Seitenauswahl ist keine Buchung mit Status „SEPA exportiert“ sichtbar.
                Die Bulk-Aktion aktualisiert nur tatsächlich passende Buchungen.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkPaidDialogOpen(false)} disabled={isBulkUpdatingPayment}>
            Abbrechen
          </Button>
          <Button
            color="success"
            variant="contained"
            onClick={handleBulkMarkSepaAsPaid}
            disabled={isBulkUpdatingPayment}
            startIcon={
              isBulkUpdatingPayment ? (
                <CircularProgress size={16} />
              ) : (
                <Icon icon="mdi:cash-check" />
              )
            }
          >
            Jetzt auf bezahlt setzen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lösch-Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Anmeldung dauerhaft löschen?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Diese Aktion kann nicht rückgängig gemacht werden. Die Anmeldung wird vollständig aus der Datenbank entfernt.
          </Alert>
          <Typography sx={{ mt: 2 }}>
            {registrationToDelete?.registrationNumber
              ? `Buchungsnummer: ${registrationToDelete.registrationNumber}`
              : ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {(registrationToDelete?.booker?.firstName || "")} {(registrationToDelete?.booker?.lastName || "")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={isDeletingRegistration}>Abbrechen</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteRegistration}
            disabled={isDeletingRegistration}
            startIcon={isDeletingRegistration ? <CircularProgress size={16} /> : <Icon icon="mdi:trash-can-outline" />}
          >
            Dauerhaft löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sensitive data dialog */}
      <Dialog open={healthDialogOpen} onClose={closeHealthDialog} maxWidth="md" fullWidth>
        <DialogTitle>Sensible Daten</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Diese Ansicht enthaelt sensible Gesundheitsdaten. Zugriff wird im Audit-Log protokolliert.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              {healthRegistration?.registrationNumber
                ? `Buchungsnummer: ${healthRegistration.registrationNumber}`
                : ""}
            </Typography>

            {isFetchingHealthData ? (
              <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {(healthDataResponse?.registration?.participants || []).map((participant, index) => {
                  const healthData = participant?.healthData || {};
                  const emergencyContact = healthData?.emergencyContact || {};
                  const pickupPersons = Array.isArray(participant?.pickupPersons)
                    ? participant.pickupPersons
                    : [];

                  return (
                    <Box
                      key={participant?.participantId || `${participant?.firstName || "p"}-${index}`}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.5,
                        p: 1.5,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {participant?.firstName || ""} {participant?.lastName || ""}
                      </Typography>
                      <Stack spacing={0.75}>
                        <Typography variant="body2">
                          <strong>Allergien:</strong> {healthData.allergies || "—"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Medikamente:</strong> {healthData.medications || "—"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Ernaehrung:</strong> {healthData.dietaryNeeds || "—"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Hinweise:</strong> {healthData.medicalNotes || "—"}
                        </Typography>
                        <Divider />
                        <Typography variant="body2">
                          <strong>Notfallkontakt:</strong>{" "}
                          {emergencyContact.name || "—"}
                          {emergencyContact.phone ? ` (${emergencyContact.phone})` : ""}
                          {emergencyContact.relationship
                            ? `, ${emergencyContact.relationship}`
                            : ""}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Abholberechtigte:</strong>{" "}
                          {pickupPersons.length > 0
                            ? pickupPersons
                                .map(
                                  (entry) =>
                                    `${entry?.name || "Unbekannt"}${
                                      entry?.phone ? ` (${entry.phone})` : ""
                                    }${entry?.notes ? ` - ${entry.notes}` : ""}`
                                )
                                .join("; ")
                            : "—"}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })}
                {(healthDataResponse?.registration?.participants || []).length === 0 && (
                  <Alert severity="info">Keine sensiblen Teilnehmerdaten vorhanden.</Alert>
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeHealthDialog}>Schliessen</Button>
        </DialogActions>
      </Dialog>
      <EditRegistrationPricingDialog
        open={Boolean(pricingEditReg)}
        onClose={() => setPricingEditReg(null)}
        registration={pricingEditReg}
        event={event}
      />
    </Box>
  );
}

EventRegistrationsTab.propTypes = {
  event: PropTypes.object.isRequired,
};
